import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Ear, SignOut, Plus, Eye, Printer, ArrowLeft, Trash, CheckCircle, Package, Cube, Users, Gear } from '@phosphor-icons/react';
import BatchCreateDialog from './BatchCreateDialog';
import DataManagementDialog from './DataManagementDialog';

export default function BatchManagement() {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [dataManageOpen, setDataManageOpen] = useState(false);
  const [detailsBatch, setDetailsBatch] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [validateConfirm, setValidateConfirm] = useState(null);
  const [validateStep2, setValidateStep2] = useState(false);

  useEffect(() => { fetchBatches(); }, [statusFilter]);

  const fetchBatches = async () => {
    try {
      let url = `${API}/batches`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      const res = await axios.get(url);
      setBatches(res.data);
    } catch (e) { toast.error('Erreur chargement des lots'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const openDetails = async (batch) => {
    try {
      const res = await axios.get(`${API}/batches/${batch.id}`);
      setDetailsBatch(res.data);
    } catch (e) { setDetailsBatch(batch); }
    setDetailsOpen(true);
  };

  const handleValidate = async () => {
    if (!validateConfirm) return;
    if (!validateStep2) { setValidateStep2(true); return; }
    try {
      await axios.post(`${API}/batches/${validateConfirm.id}/validate`);
      toast.success(`Lot ${validateConfirm.lot_number} validé`);
      fetchBatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur validation'); }
    setValidateConfirm(null);
    setValidateStep2(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/batches/${deleteConfirm.id}`);
      toast.success('Lot supprimé');
      fetchBatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur suppression'); }
    setDeleteConfirm(null);
  };

  const openLabelsPdf = async (batch) => {
    try {
      const res = await axios.get(`${API}/batches/${batch.id}/labels-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) { toast.error('Erreur génération PDF'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  const stats = {
    total: batches.length,
    draft: batches.filter(b => b.status === 'draft').length,
    validated: batches.filter(b => b.status === 'validated').length,
    totalPieces: batches.filter(b => b.status === 'validated').reduce((sum, b) => sum + (b.total_pieces || 0), 0),
  };

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="batch-management">
      <header className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Ear size={24} weight="duotone" className="sm:w-7 sm:h-7" />
            <span className="font-heading font-bold text-base sm:text-lg tracking-tight">HAMAC</span>
            <span className="text-zinc-500 text-xs sm:text-sm ml-1 sm:ml-2 hidden sm:inline">Lots de fabrication</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/dashboard"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white"><ArrowLeft size={18} className="sm:mr-2" /><span className="hidden sm:inline">Dashboard</span></Button></Link>
            {isAdmin && <Link to="/admin"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white"><Users size={18} className="sm:mr-2" /><span className="hidden sm:inline">Admin</span></Button></Link>}
            <span className="text-xs sm:text-sm text-zinc-400 hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-white" data-testid="logout-btn"><SignOut size={18} /></Button>
          </div>
        </div>
      </header>

      {/* Stats bar */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3"><Package size={20} className="text-zinc-600 sm:w-6 sm:h-6 flex-shrink-0" /><div><p className="text-lg sm:text-2xl font-bold">{stats.total}</p><p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Total</p></div></div>
            <div className="flex items-center gap-2 sm:gap-3"><Cube size={20} className="text-amber-500 sm:w-6 sm:h-6 flex-shrink-0" /><div><p className="text-lg sm:text-2xl font-bold">{stats.draft}</p><p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Brouillons</p></div></div>
            <div className="flex items-center gap-2 sm:gap-3"><CheckCircle size={20} className="text-emerald-600 sm:w-6 sm:h-6 flex-shrink-0" /><div><p className="text-lg sm:text-2xl font-bold">{stats.validated}</p><p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Validés</p></div></div>
            <div className="flex items-center gap-2 sm:gap-3"><Printer size={20} className="text-blue-600 sm:w-6 sm:h-6 flex-shrink-0" /><div><p className="text-lg sm:text-2xl font-bold">{stats.totalPieces}</p><p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Pièces</p></div></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Button onClick={() => setCreateOpen(true)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-xs sm:text-sm" data-testid="create-batch-btn">
            <Plus size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Créer un lot</span>
            <span className="sm:hidden">Lot</span>
          </Button>
          <Button variant="outline" onClick={() => setDataManageOpen(true)} className="rounded-sm text-xs sm:text-sm" data-testid="manage-data-btn">
            <Gear size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Gérer les données</span>
            <span className="sm:hidden">Données</span>
          </Button>
          <div className="flex-1" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 sm:w-48 rounded-sm bg-white text-xs sm:text-sm" data-testid="batch-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="draft">Brouillons</SelectItem>
              <SelectItem value="validated">Validés</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-zinc-200 overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center"><div className="spinner mx-auto mb-4" />Chargement...</div>
          ) : batches.length === 0 ? (
            <div className="p-12 text-center text-zinc-500"><Package size={48} className="mx-auto mb-4 text-zinc-300" /><p>Aucun lot de fabrication</p></div>
          ) : (
            <Table className="dense-table">
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="label-overline">N° Lot</TableHead>
                  <TableHead className="label-overline hidden sm:table-cell">Date</TableHead>
                  <TableHead className="label-overline hidden md:table-cell">Résine</TableHead>
                  <TableHead className="label-overline">Couleur</TableHead>
                  <TableHead className="label-overline hidden lg:table-cell">Modèles</TableHead>
                  <TableHead className="label-overline">Pièces</TableHead>
                  <TableHead className="label-overline hidden sm:table-cell">Post-trait.</TableHead>
                  <TableHead className="label-overline">Statut</TableHead>
                  <TableHead className="label-overline text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map(b => (
                  <TableRow key={b.id} className={`table-row-hover ${b.status === 'draft' ? 'bg-amber-50/30' : ''}`} data-testid={`batch-row-${b.id}`}>
                    <TableCell className="font-mono font-bold text-xs sm:text-sm">{b.lot_number}</TableCell>
                    <TableCell className="text-sm hidden sm:table-cell">{formatDate(b.fabrication_date)}</TableCell>
                    <TableCell className="text-sm hidden md:table-cell">{b.resin_brand} {b.resin_model}</TableCell>
                    <TableCell className="text-sm">{b.color_name}{b.pigment_name ? ` (${b.pigment_name})` : ''}</TableCell>
                    <TableCell className="text-sm hidden lg:table-cell">{b.models?.map(m => m.model_name).join(', ')}</TableCell>
                    <TableCell className="font-bold text-sm">{b.total_pieces}</TableCell>
                    <TableCell className="text-sm hidden sm:table-cell">{b.post_treatment}</TableCell>
                    <TableCell>
                      {b.status === 'draft' ? (
                        <span className="px-2 py-1 bg-amber-100 text-amber-800 text-[10px] sm:text-xs font-medium rounded-sm uppercase tracking-wider">Brouillon</span>
                      ) : (
                        <span className="badge-completed text-[10px] sm:text-xs">Validé</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-0.5 sm:gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openDetails(b)} title="Détails" data-testid={`batch-details-${b.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9"><Eye size={16} /></Button>
                        {b.status === 'validated' && (
                          <Button variant="ghost" size="sm" onClick={() => openLabelsPdf(b)} title="Imprimer étiquettes" data-testid={`batch-print-${b.id}`} className="h-8 w-8 p-0 sm:h-9 sm:w-9"><Printer size={16} /></Button>
                        )}
                        {b.status === 'draft' && (
                          <>
                            <Button size="sm" onClick={() => { setValidateConfirm(b); setValidateStep2(false); }} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-[11px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3" data-testid={`batch-validate-${b.id}`}>
                              <CheckCircle size={14} className="mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Valider</span>
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(b)} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 sm:h-9 sm:w-9" title="Supprimer" data-testid={`batch-delete-${b.id}`}>
                              <Trash size={16} />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Batch Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye size={24} />Détails du lot {detailsBatch?.lot_number}</DialogTitle>
          </DialogHeader>
          {detailsBatch && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-zinc-50 p-3 rounded-sm border"><p className="text-xs text-zinc-400 uppercase tracking-wider">N° Lot</p><p className="font-mono font-bold text-base sm:text-lg">{detailsBatch.lot_number}</p></div>
                <div className="bg-zinc-50 p-3 rounded-sm border"><p className="text-xs text-zinc-400 uppercase tracking-wider">Date de fabrication</p><p className="font-bold">{formatDate(detailsBatch.fabrication_date)}</p></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-zinc-50 p-3 rounded-sm border"><p className="text-xs text-zinc-400 uppercase tracking-wider">Résine</p><p className="font-semibold">{detailsBatch.resin_brand} {detailsBatch.resin_model}</p></div>
                <div className="bg-zinc-50 p-3 rounded-sm border"><p className="text-xs text-zinc-400 uppercase tracking-wider">Couleur</p><p className="font-semibold">{detailsBatch.color_name}{detailsBatch.pigment_name ? ` (${detailsBatch.pigment_name})` : ''}</p></div>
              </div>
              <div className="bg-zinc-50 p-3 rounded-sm border"><p className="text-xs text-zinc-400 uppercase tracking-wider">Post-traitement</p><p className="font-semibold">{detailsBatch.post_treatment}</p></div>
              <div className="border rounded-sm">
                <div className="bg-zinc-900 text-white px-4 py-2"><p className="font-heading font-semibold text-sm">Modèles produits</p></div>
                <div className="p-3">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b"><th className="text-left py-1 text-xs text-zinc-500 uppercase">Modèle</th><th className="text-right py-1 text-xs text-zinc-500 uppercase">Quantité</th></tr></thead>
                    <tbody>{detailsBatch.models?.map((m, i) => <tr key={i} className="border-b last:border-0"><td className="py-2 font-medium">{m.model_name}</td><td className="py-2 text-right font-bold">{m.quantity}</td></tr>)}</tbody>
                    <tfoot><tr><td className="py-2 font-bold">Total</td><td className="py-2 text-right font-bold text-lg">{detailsBatch.total_pieces}</td></tr></tfoot>
                  </table>
                </div>
              </div>
              {detailsBatch.notes && <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm"><p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Notes</p><p className="text-sm">{detailsBatch.notes}</p></div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm text-zinc-500">
                <div>Créé par : <strong>{detailsBatch.created_by}</strong></div>
                {detailsBatch.validated_by && <div>Validé par : <strong>{detailsBatch.validated_by}</strong> le {formatDate(detailsBatch.validated_at)}</div>}
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDetailsOpen(false)} className="rounded-sm">Fermer</Button>
            {detailsBatch?.status === 'validated' && (
              <Button onClick={() => openLabelsPdf(detailsBatch)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800"><Printer size={16} className="mr-2" />Imprimer étiquettes</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validate Confirmation Dialog (2 steps) */}
      <Dialog open={!!validateConfirm} onOpenChange={() => { setValidateConfirm(null); setValidateStep2(false); }}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{validateStep2 ? 'Confirmation finale' : 'Valider le lot'}</DialogTitle>
            <DialogDescription>
              {validateStep2
                ? `Vous confirmez la validation DÉFINITIVE du lot ${validateConfirm?.lot_number}. Cette action est IRRÉVERSIBLE.`
                : `Vous êtes sur le point de valider définitivement ce lot. Cette action est irréversible. Continuer ?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setValidateConfirm(null); setValidateStep2(false); }} className="rounded-sm">Annuler</Button>
            <Button onClick={handleValidate} className={`rounded-sm text-white ${validateStep2 ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid="confirm-validate-batch">
              {validateStep2 ? 'JE CONFIRME' : 'Valider le lot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer le lot</DialogTitle>
            <DialogDescription>Êtes-vous sûr de vouloir supprimer le brouillon {deleteConfirm?.lot_number} ?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleDelete} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-batch">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Batch Dialog */}
      <BatchCreateDialog open={createOpen} onOpenChange={setCreateOpen} onSuccess={fetchBatches} />

      {/* Data Management Dialog */}
      <DataManagementDialog open={dataManageOpen} onOpenChange={setDataManageOpen} />
    </div>
  );
}
